import React from "react";
import { MapPin, Bed, Bath, Car, Square, DollarSign, Calendar, Building, Eye, Users } from 'lucide-react';
import moment from "moment";
import LazyImage from "../common/LazyImage";

const REAPropertyCard = ({ property, isSelected, onClick, viewMode = "grid" }) => {
  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    if (numPrice === 0) return "Price on Application";
    if (numPrice >= 1000000) {
      return `$${(numPrice / 1000000).toFixed(1)}M`;
    }
    if (numPrice >= 1000) {
      return `$${(numPrice / 1000).toFixed(0)}K`;
    }
    return `$${numPrice.toLocaleString()}`;
  };

  //  Simplified color system with minimal palette
  const getListingTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'residential':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'commercial':
        return 'bg-slate-50 text-slate-700 border border-slate-200';
      case 'land':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      default:
        return 'bg-slate-50 text-slate-700 border border-slate-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'house':
        return Building;
      case 'apartment':
      case 'unit':
        return Building;
      default:
        return Building;
    }
  };

  const getPropertyFeatures = (features) => {
    if (!features) return [];

    const featuresList = [];
    if (features.bedrooms && parseInt(features.bedrooms) > 0) {
      featuresList.push({ icon: Bed, value: features.bedrooms, label: 'bed' });
    }
    if (features.bathrooms && parseInt(features.bathrooms) > 0) {
      featuresList.push({ icon: Bath, value: features.bathrooms, label: 'bath' });
    }
    if (features.garages && parseInt(features.garages) > 0) {
      featuresList.push({ icon: Car, value: features.garages, label: 'car' });
    }

    return featuresList;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const features = getPropertyFeatures(property.features);
  const CategoryIcon = getCategoryIcon(property.category);

  // Extract main image with multiple fallback options
  const getMainImage = () => {
    // Try extra_info.images first (array of URLs or objects)
    if (property.extra_info?.images && property.extra_info.images.length > 0) {
      const firstImage = property.extra_info.images[0];
      // Handle both string URLs and object with file/url property
      if (typeof firstImage === 'string' && firstImage.trim()) {
        return firstImage.trim();
      } else if (firstImage && typeof firstImage === 'object') {
        return firstImage.file || firstImage.url || firstImage.src || null;
      }
    }

    // Try direct image fields
    if (property.image && typeof property.image === 'string' && property.image.trim()) {
      return property.image.trim();
    }

    if (property.photo_url && typeof property.photo_url === 'string' && property.photo_url.trim()) {
      return property.photo_url.trim();
    }

    if (property.thumbnail_url && typeof property.thumbnail_url === 'string' && property.thumbnail_url.trim()) {
      return property.thumbnail_url.trim();
    }

    // Try images array at root level
    if (property.images && Array.isArray(property.images) && property.images.length > 0) {
      const firstImage = property.images[0];
      if (typeof firstImage === 'string' && firstImage.trim()) {
        return firstImage.trim();
      } else if (firstImage && typeof firstImage === 'object') {
        return firstImage.file || firstImage.url || firstImage.src || null;
      }
    }

    return null;
  };

  const mainImage = getMainImage();

  if (viewMode === "list") {
    return (
      <div
        onClick={onClick}
        className={`bg-white rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-lg ${isSelected
            ? 'border-blue-500 shadow-lg ring-2 ring-blue-100'
            : 'border-slate-200 hover:border-slate-300'
          }`}
      >
        <div className="p-6">
          <div className="flex items-start space-x-6">
            {/* Image */}
            <div className="flex-shrink-0">
              <LazyImage
                src={mainImage || "/api/placeholder/300/200"}
                alt={property.headline || "Property image"}
                className="w-28 h-24 object-cover rounded-lg"
                errorImage="/api/placeholder/300/200"
                style={{ minHeight: '6rem' }}
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0 pr-4">
                  <h3 className="font-semibold text-slate-900 text-base line-clamp-2 leading-tight">
                    {property.headline}
                  </h3>
                  <div className="flex items-start text-slate-500 text-sm mt-1">
                    <MapPin className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-2 leading-relaxed">
                      {property.street_number} {property.street}, {property.suburb} {property.state} {property.postcode}
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-emerald-600 text-lg">
                    {formatPrice(property.price)}
                  </div>
                  <div className="text-xs text-slate-400">
                    ID: {property.listing_id}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Property Type */}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getListingTypeColor(property.listing_type)}`}>
                    {property.listing_type}
                  </span>

                  {/* Category */}
                  {property.category && (
                    <span className="text-xs text-slate-600 bg-slate-100 px-3 py-1 rounded-full border border-slate-200 whitespace-nowrap">
                      {property.category}
                    </span>
                  )}

                  {/* Features */}
                  {features.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3 text-slate-600">
                      {features.slice(0, 3).map((feature, index) => {
                        const FeatureIcon = feature.icon;
                        return (
                          <div key={index} className="flex items-center text-sm whitespace-nowrap">
                            <FeatureIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span>{feature.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="text-xs text-slate-400 flex-shrink-0">
                  {moment(property.modified_time).format('MMM DD, YYYY')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-lg ${isSelected
          ? 'border-blue-500 shadow-lg ring-2 ring-blue-100'
          : 'border-slate-200 hover:border-slate-300'
        }`}
    >
      {/* Image */}
      <div className="relative">
        <LazyImage
          src={mainImage || "/api/placeholder/300/200"}
          alt={property.headline || "Property image"}
          className="w-full h-48 object-cover rounded-t-xl"
          errorImage="/api/placeholder/300/200"
          style={{ minHeight: '12rem' }}
        />

        {/* Price Badge */}
        <div className="absolute top-4 left-4 z-20">
          <span className="bg-white px-4 py-2 rounded-full text-sm font-bold text-emerald-600 shadow-lg">
            {formatPrice(property.price)}
          </span>
        </div>

        {/* Listing Type Badge */}
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getListingTypeColor(property.listing_type)}`}>
            {property.listing_type}
          </span>
        </div>

        {/* Status Badges - Positioned below price badge */}
        <div className="absolute top-16 left-4 flex flex-col gap-1.5 z-10">
          {/* Sold Badge */}
          {(property.is_sold || property?.status === 'Sold' || property?.status?.name === 'Sold') && (
            <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg shadow-green-600/30">
              Sold
            </div>
          )}
          {/* Withdrawn Badge */}
          {property.is_withdrawn && (
            <div className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg shadow-amber-500/30">
              Withdrawn
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 text-base mb-2 line-clamp-2">
            {property.headline}
          </h3>
          <div className="flex items-center text-slate-500 text-sm">
            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">
              {property.street_number} {property.street}, {property.suburb} {property.state} {property.postcode}
            </span>
          </div>
        </div>

        {/* Features */}
        {features.length > 0 && (
          <div className="flex items-center space-x-4 mb-4 text-slate-600">
            {features.slice(0, 3).map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div key={index} className="flex items-center text-sm">
                  <FeatureIcon className="w-4 h-4 mr-1.5" />
                  <span>{feature.value}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Description */}
        {property.description && (
          <p className="text-slate-600 text-sm mb-4 line-clamp-2 leading-relaxed">
            {truncateText(property.description, 80)}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex items-center space-x-3">
            {property.category && (
              <div className="flex items-center text-sm text-slate-600">
                <CategoryIcon className="w-4 h-4 mr-1.5" />
                <span>{property.category}</span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 text-sm text-slate-400">
            <Calendar className="w-4 h-4" />
            <span>{moment(property.modified_time).format('MMM DD')}</span>
          </div>
        </div>

        {/* Property ID */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-400">
            Listing ID: {property.listing_id}
          </div>
        </div>
      </div>
    </div>
  );
};

export default REAPropertyCard;